import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Shield, Mic, Volume2, KeyRound } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleColors = {
  owner: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  admin: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
  user: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
};

export default function UsersPage() {
  const { token, user: currentUser, tenant } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isResetPwdOpen, setIsResetPwdOpen] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [resetPwdValue, setResetPwdValue] = useState('');
  const [resetPwdLoading, setResetPwdLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'user',
    department_ids: []
  });

  const canManage = currentUser?.role === 'owner' || currentUser?.role === 'admin';
  const isOwner = currentUser?.role === 'owner';

  useEffect(() => {
    if (canManage) {
      fetchUsers();
      fetchDepartments();
    }
  }, [canManage]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data);
    } catch { /* silent */ }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/users`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdate = async () => {
    try {
      const updateData = { ...formData };
      delete updateData.password;
      delete updateData.email;
      
      await axios.patch(`${API}/users/${selectedUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully');
      setIsEditOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openResetPwd = (user) => {
    setResetPwdUser(user);
    setResetPwdValue('');
    setIsResetPwdOpen(true);
  };

  const handleResetPassword = async () => {
    if (resetPwdValue.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    setResetPwdLoading(true);
    try {
      await axios.post(`${API}/users/${resetPwdUser.id}/reset-password`, {
        new_password: resetPwdValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Mot de passe reinitialise pour ${resetPwdUser.first_name} ${resetPwdUser.last_name}`);
      setIsResetPwdOpen(false);
      setResetPwdUser(null);
      setResetPwdValue('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la reinitialisation');
    } finally {
      setResetPwdLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: 'user',
      department_ids: []
    });
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
      department_ids: user.department_ids || []
    });
    setIsEditOpen(true);
  };

  const filteredUsers = users.filter(user =>
    user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tenantFeatures = tenant?.features || {};

  const toggleUserFeature = async (userId, feature, enabled) => {
    try {
      await axios.patch(`${API}/tenant/users/${userId}/features`, {
        feature,
        enabled,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Licence ${feature === 'voice_clone' ? 'Premium' : 'Standard'} ${enabled ? 'activee' : 'desactivee'}`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout title="Users">
        <Card className="p-8 text-center">
          <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Access Denied</h3>
          <p className="text-muted-foreground mt-2">You don't have permission to view this page.</p>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Users">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-muted-foreground">Manage your team members and their roles</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
                data-testid="search-users"
              />
            </div>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="create-user-btn">
                  <Plus className="w-4 h-4" />
                  Invite User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite New User</DialogTitle>
                  <DialogDescription>Add a new team member to your organization</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        placeholder="John"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        data-testid="user-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        placeholder="Doe"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        data-testid="user-last-name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="user-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Min. 6 characters"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      data-testid="user-password"
                    />
                  </div>
                  {isOwner && (
                    <div className="space-y-2">
                      <Label htmlFor="role">Role</Label>
                      <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                        <SelectTrigger data-testid="user-role-select">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {departments.length > 0 && (
                    <div className="space-y-2">
                      <Label>Departements</Label>
                      <div className="flex flex-wrap gap-2">
                        {departments.map(d => {
                          const selected = (formData.department_ids || []).includes(d.id);
                          return (
                            <Badge
                              key={d.id}
                              variant={selected ? 'default' : 'outline'}
                              className="cursor-pointer gap-1.5 select-none"
                              style={selected ? { backgroundColor: d.color, borderColor: d.color } : {}}
                              onClick={() => {
                                const ids = formData.department_ids || [];
                                setFormData({
                                  ...formData,
                                  department_ids: selected ? ids.filter(x => x !== d.id) : [...ids, d.id]
                                });
                              }}
                              data-testid={`create-dept-toggle-${d.id}`}
                            >
                              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: selected ? '#fff' : d.color }} />
                              {d.name}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={handleCreate} 
                    disabled={!formData.first_name || !formData.last_name || !formData.email || !formData.password}
                    data-testid="save-user-btn"
                  >
                    Create User
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Users Table */}
        <Card data-testid="users-table-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Departements</TableHead>
                  <TableHead>Status</TableHead>
                  {isOwner && (tenantFeatures.voice_standard || tenantFeatures.voice_clone) && (
                    <TableHead className="text-center">Licences Voix</TableHead>
                  )}
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isOwner && (tenantFeatures.voice_standard || tenantFeatures.voice_clone) ? 7 : 6} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isOwner && (tenantFeatures.voice_standard || tenantFeatures.voice_clone) ? 7 : 6} className="text-center py-8 text-muted-foreground">
                      {searchQuery ? 'No users found matching your search' : 'No users yet.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} className="group" data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-emerald-500/10 text-emerald-600">
                              {`${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{user.first_name} {user.last_name}</p>
                            {user.id === currentUser?.id && (
                              <span className="text-xs text-muted-foreground">(You)</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={roleColors[user.role]}>
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(user.department_ids || []).map(dId => {
                            const dept = departments.find(d => d.id === dId);
                            if (!dept) return null;
                            return (
                              <Badge key={dId} variant="outline" className="text-[11px] gap-1" data-testid={`user-dept-badge-${dId}`}>
                                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: dept.color }} />
                                {dept.name}
                              </Badge>
                            );
                          })}
                          {(!user.department_ids || user.department_ids.length === 0) && (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_active ? 'default' : 'secondary'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      {isOwner && (tenantFeatures.voice_standard || tenantFeatures.voice_clone) && (
                        <TableCell>
                          <div className="flex items-center justify-center gap-4">
                            {tenantFeatures.voice_standard && (
                              <div className="flex items-center gap-1.5" data-testid={`user-voice-std-${user.id}`}>
                                <Mic className="w-3 h-3 text-blue-500" />
                                <span className="text-[10px] text-muted-foreground">Std</span>
                                <Switch
                                  checked={user.features ? user.features.voice_standard === true : true}
                                  onCheckedChange={(val) => toggleUserFeature(user.id, 'voice_standard', val)}
                                  className="h-4 w-8"
                                  data-testid={`toggle-std-${user.id}`}
                                />
                              </div>
                            )}
                            {tenantFeatures.voice_clone && (
                              <div className="flex items-center gap-1.5" data-testid={`user-voice-prem-${user.id}`}>
                                <Volume2 className="w-3 h-3 text-violet-500" />
                                <span className="text-[10px] text-muted-foreground">Prem</span>
                                <Switch
                                  checked={user.features ? user.features.voice_clone === true : true}
                                  onCheckedChange={(val) => toggleUserFeature(user.id, 'voice_clone', val)}
                                  className="h-4 w-8"
                                  data-testid={`toggle-prem-${user.id}`}
                                />
                              </div>
                            )}
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        {user.id !== currentUser?.id && user.role !== 'owner' && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`user-actions-${user.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEdit(user)} data-testid={`edit-user-${user.id}`}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openResetPwd(user)} data-testid={`reset-pwd-user-${user.id}`}>
                                <KeyRound className="w-4 h-4 mr-2" />
                                Reinitialiser le mdp
                              </DropdownMenuItem>
                              {isOwner && (
                                <DropdownMenuItem onClick={() => handleDelete(user.id)} className="text-destructive" data-testid={`delete-user-${user.id}`}>
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update user information</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-first_name">First Name *</Label>
                  <Input
                    id="edit-first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    data-testid="edit-user-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-last_name">Last Name *</Label>
                  <Input
                    id="edit-last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    data-testid="edit-user-last-name"
                  />
                </div>
              </div>
              {isOwner && (
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Role</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger data-testid="edit-user-role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {departments.length > 0 && (
                <div className="space-y-2">
                  <Label>Departements</Label>
                  <div className="flex flex-wrap gap-2">
                    {departments.map(d => {
                      const selected = (formData.department_ids || []).includes(d.id);
                      return (
                        <Badge
                          key={d.id}
                          variant={selected ? 'default' : 'outline'}
                          className="cursor-pointer gap-1.5 select-none"
                          style={selected ? { backgroundColor: d.color, borderColor: d.color } : {}}
                          onClick={() => {
                            const ids = formData.department_ids || [];
                            setFormData({
                              ...formData,
                              department_ids: selected ? ids.filter(x => x !== d.id) : [...ids, d.id]
                            });
                          }}
                          data-testid={`edit-dept-toggle-${d.id}`}
                        >
                          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: selected ? '#fff' : d.color }} />
                          {d.name}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdate} disabled={!formData.first_name || !formData.last_name} data-testid="update-user-btn">
                Update User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={isResetPwdOpen} onOpenChange={setIsResetPwdOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reinitialiser le mot de passe</DialogTitle>
              <DialogDescription>
                Definir un nouveau mot de passe pour {resetPwdUser?.first_name} {resetPwdUser?.last_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reset-password">Nouveau mot de passe</Label>
                <Input
                  id="reset-password"
                  type="password"
                  placeholder="Min. 6 caracteres"
                  value={resetPwdValue}
                  onChange={(e) => setResetPwdValue(e.target.value)}
                  data-testid="reset-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsResetPwdOpen(false)}>Annuler</Button>
              <Button
                onClick={handleResetPassword}
                disabled={resetPwdLoading || resetPwdValue.length < 6}
                data-testid="confirm-reset-password-btn"
              >
                {resetPwdLoading ? 'Reinitialisation...' : 'Reinitialiser'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
